import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Trophy, Package, Clock, Check, CheckCheck, Heart, Tag, BookOpen } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { formatDate } from '@/utils';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  related_entity_type?: string;
  related_entity_id?: string;
  is_read: boolean;
  created_at: string;
}

const TYPE_META: Record<string, { icon: React.FC<{ className?: string }>; color: string }> = {
  auction_won:     { icon: Trophy,   color: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-600' },
  auction_ended:   { icon: Package,  color: 'bg-brand-100 dark:bg-brand-900/40 text-brand-600 dark:text-brand-400' },
  auction_expired: { icon: Clock,    color: 'bg-gray-100 dark:bg-zinc-800 text-gray-500 dark:text-zinc-500' },
  wishlist_match:   { icon: Heart,     color: 'bg-pink-100 dark:bg-pink-900/40 text-pink-600' },
  wishlist_seller:  { icon: Tag,       color: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600' },
  set_watch_match:  { icon: BookOpen,  color: 'bg-brand-100 dark:bg-brand-900/40 text-brand-600 dark:text-brand-400' },
};

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchNotifications = async () => {
    try {
      const response = await apiClient.get<any>('/notifications');
      setNotifications(response.data?.data ?? response.data ?? []);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = async (id: string) => {
    try {
      await apiClient.put(`/notifications/${id}/read`, {});
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch (error) { console.error(error); }
  };

  const markAllAsRead = async () => {
    try {
      await apiClient.put('/notifications/read-all', {});
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) { console.error(error); }
  };

  useEffect(() => { fetchNotifications(); }, []);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="bg-white dark:bg-zinc-900 min-h-screen">
      <div className="bg-gradient-to-r from-brand-600 to-accent-600 text-white py-10">
        <div className="container-custom flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-display font-bold mb-1">Notifications</h1>
            <p className="text-brand-100">
              {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
            </p>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="flex items-center gap-2 bg-white dark:bg-zinc-900/20 hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900/30 text-white px-4 py-2 rounded-xl text-sm font-medium transition-colors"
            >
              <CheckCheck className="h-4 w-4" />
              Mark all read
            </button>
          )}
        </div>
      </div>

      <div className="container-custom py-8 max-w-3xl">
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => <div key={i} className="skeleton h-20 rounded-xl" />)}
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-24">
            <Bell className="h-16 w-16 mx-auto mb-4 text-gray-400 dark:text-zinc-500" />
            <h3 className="text-xl font-bold text-gray-700 dark:text-zinc-300 mb-2">No notifications yet</h3>
            <p className="text-gray-500 dark:text-zinc-500">You'll be notified about auction results and more</p>
          </div>
        ) : (
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm overflow-hidden divide-y divide-gray-200 dark:divide-zinc-800">
            {notifications.map((notif) => {
              const meta = TYPE_META[notif.type] ?? { icon: Bell, color: 'bg-brand-100 dark:bg-brand-900/40 text-brand-600 dark:text-brand-400' };
              const Icon = meta.icon;
              return (
                <div
                  key={notif.id}
                  className={`flex items-start gap-4 p-4 transition-colors ${!notif.is_read ? 'bg-brand-50 dark:bg-brand-900/20' : 'hover:bg-white dark:hover:bg-zinc-800 dark:bg-zinc-900'}`}
                >
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                    <Icon className="h-5 w-5" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold text-sm ${!notif.is_read ? 'text-gray-900 dark:text-zinc-100' : 'text-gray-700 dark:text-zinc-300'}`}>
                      {notif.title}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-zinc-400 mt-0.5">{notif.message}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-gray-500 dark:text-zinc-500">{formatDate(notif.created_at, 'MMM d, h:mm a')}</span>
                      {notif.related_entity_type === 'listing' && notif.related_entity_id && (
                        <Link to={`/listings/${notif.related_entity_id}`} className="text-xs text-brand-600 dark:text-brand-400 hover:underline">
                          View Listing →
                        </Link>
                      )}
                    </div>
                  </div>

                  {!notif.is_read && (
                    <button
                      onClick={() => markAsRead(notif.id)}
                      className="text-gray-500 dark:text-zinc-500 hover:text-brand-600 dark:text-brand-400 p-1 flex-shrink-0"
                      title="Mark as read"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
